import type { CSSProperties, ReactNode } from 'react';
import type { RichDoc, RichMark, RichNode } from '@os/doc';
import { safeColor, safeHref } from './sanitize';

const ALIGN = new Set(['left', 'center', 'right', 'justify']);

function alignStyle(n: RichNode): CSSProperties | undefined {
  const a = n.attrs?.textAlign;
  return typeof a === 'string' && ALIGN.has(a) ? { textAlign: a as CSSProperties['textAlign'] } : undefined;
}

function withMark(mark: RichMark, child: ReactNode, key: number): ReactNode {
  switch (mark.type) {
    case 'bold':
      return <strong key={key}>{child}</strong>;
    case 'italic':
      return <em key={key}>{child}</em>;
    case 'underline':
      return <u key={key}>{child}</u>;
    case 'strike':
      return <s key={key}>{child}</s>;
    case 'link': {
      const href = safeHref(mark.attrs?.href);
      return href ? (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {child}
        </a>
      ) : (
        child
      );
    }
    case 'textStyle': {
      const color = safeColor(mark.attrs?.color);
      return color ? (
        <span key={key} style={{ color }}>
          {child}
        </span>
      ) : (
        child
      );
    }
    default:
      return child;
  }
}

function renderNode(n: RichNode, key: number): ReactNode {
  const kids = () => (n.content ?? []).map(renderNode);
  switch (n.type) {
    case 'text':
      // Marks wrap outward: the first mark is the outermost element.
      return <span key={key}>{(n.marks ?? []).reduceRight<ReactNode>((acc, m, i) => withMark(m, acc, i), n.text ?? '')}</span>;
    case 'paragraph':
      return (
        <p key={key} style={alignStyle(n)}>
          {kids()}
        </p>
      );
    case 'heading': {
      const level = n.attrs?.level;
      const Tag = level === 1 ? 'h1' : level === 2 ? 'h2' : level === 3 ? 'h3' : 'p';
      return (
        <Tag key={key} style={alignStyle(n)}>
          {kids()}
        </Tag>
      );
    }
    case 'bulletList':
      return <ul key={key}>{kids()}</ul>;
    case 'orderedList':
      return <ol key={key}>{kids()}</ol>;
    case 'listItem':
      return <li key={key}>{kids()}</li>;
    case 'taskList':
      return (
        <ul key={key} data-type="taskList">
          {kids()}
        </ul>
      );
    case 'taskItem':
      return (
        <li key={key} data-type="taskItem" data-checked={n.attrs?.checked === true}>
          <input type="checkbox" checked={n.attrs?.checked === true} disabled readOnly tabIndex={-1} aria-hidden="true" />
          <div>{kids()}</div>
        </li>
      );
    case 'hardBreak':
      return <br key={key} />;
    default:
      // Unknown node (a newer client, a forged peer): keep its text, never its markup.
      return <span key={key}>{kids()}</span>;
  }
}

/**
 * Static rendering of rich text from its JSON (no editor mounted): an allow-list of nodes and marks, safe links and
 * colors only. A pre-v2 object without content shows its plain text, one paragraph per line.
 */
export function RichText({ doc, fallback }: { doc: RichDoc | null | undefined; fallback: string }) {
  if (!doc) return <div className="rich">{fallback.split('\n').map((line, i) => <p key={i}>{line}</p>)}</div>;
  return <div className="rich">{doc.content.map(renderNode)}</div>;
}
